<template>
  <div class="precision-form-item__account">
    <UIDropdownSearchList
      v-if="isDropdownVisible"
      :data="listValues"
      placement="bottom-start"
      @click:item="handleAddOwner"
    >
      <template #default="{ show }">
        <UIButton
          label="Select owner"
          size="sm"
          variant="gray"
          fill="outlined"
          :trailing="ChevronDownIcon"
          @click="show"
        />
      </template>
    </UIDropdownSearchList>
    <span v-else>{{ accountName }}</span>
  </div>
  <div class="precision-form-item__amount">
    <UIInputPercent v-model="amount" size="sm" @ready="handleReady" />
  </div>
  <div class="precision-form-item__asset">
    <span>{{ assetName }}</span>
    <UIButton
      v-if="isJustCreated"
      v-tooltip.top="'Delete owner'"
      variant="danger"
      fill="light"
      size="sm"
      :leading="TrashIcon"
      @click="handleClickDelete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { DropdownListData } from '@types'
import { OrganizeFamilyStructureAmountItem } from '../utils/types'

import { ASSET_FIELD, UNKNOWN_VALUE } from '@/const'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import { UIInputPercent } from '@ui/fields'
import { UIButton } from '@ui/buttons'
import { ChevronDownIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { UIDropdownSearchList } from '@ui/dropdowns'

type Props = {
  index: number
  item: OrganizeFamilyStructureAmountItem
  listValues: DropdownListData[]
  isJustCreated: boolean
  assetName: string
}

type Emits = {
  update: [index: number, data: OrganizeFamilyStructureAmountItem]
  delete: [index: number]
}

const emit = defineEmits<Emits>()
const props = defineProps<Props>()

const assetsBunchStore = useAssetsBunchStore()

const accountId = computed({
  get: () => props.item.accountId,
  set: (value: string | undefined) => {
    emit('update', props.index, { accountId: value, amount: props.item.amount })
  },
})

const amount = computed({
  get: () => props.item.amount,
  set: (value: number) => {
    emit('update', props.index, { accountId: props.item.accountId, amount: value })
  },
})

const accountName = computed(() => {
  if (!accountId.value) return UNKNOWN_VALUE
  const account = assetsBunchStore.getElementById(accountId.value)
  return account?.field<string>(ASSET_FIELD.NAME).value ?? UNKNOWN_VALUE
})

const isDropdownVisible = computed(() => !accountId.value)

const handleReady = (el: HTMLInputElement) => {
  if (props.index !== 0) return
  el.focus()
}

const handleAddOwner = (data?: string) => {
  if (!data) return
  accountId.value = data
}

const handleClickDelete = () => {
  emit('delete', props.index)
}
</script>

<style scoped>
.precision-form-item {
  &__account,
  &__amount,
  &__asset {
    @apply flex items-center py-1;
    @apply overflow-hidden;
  }

  &__account {

    > span {
      @apply text-xs font-medium text-right;
      @apply truncate;
    }

    .v-popper,
    .ui-button {
      @apply w-full;
    }

    :deep(.ui-button__label) {
      @apply flex-auto;
      @apply text-left;
    }
  }

  &__asset {
    @apply justify-between;

    > span {
      @apply text-xs font-medium;
      @apply truncate;
    }
  }

  &__amount {
    @apply gap-x-0.5;
  }
}
</style>
